import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import NameModal from '../../components/modals/NameModal';
import LinktreeButton from '../../components/oracle/LinktreeButton';

// Iconos e imágenes
import { FaInstagram, FaYoutube } from "react-icons/fa";
import { FaTiktok } from "react-icons/fa6"; 
import perfilImg from '../../assets/images/perfil.png';
import merkabaImg from '../../assets/images/merkaba/Merkaba.png';
import { sendCodedLog } from '../../services/logService';
import Common from '../../common/Common';
import { getRandomCardImage } from '../../assets/oracle/deck';

// --- ITEMS DESTACADOS (ORACLE) ---
const memenuItems = [
  {
    title: "Five Star Reading",
    subtitle: "Get a complete reading for a specific topic",
    cardCount: 5,
    layout: 'cross', // <--- Disposición en estrella / X de 5 cartas
    path: "/oracle/reading/star",
    featured: true,
  },
  {
    title: "Daily Card",
    subtitle: "Receive guide for your spiritual path",
    image: getRandomCardImage(),
    path: "/oracle/reading/today",
    featured: true,
  },
  {
    title: "Portal Oracle",
    subtitle: "Seal across the Universal Laws",
    image: getRandomCardImage(),
    path: "/oracle",
    featured: true,
  },
];

const baseMenuItems = [];

const OracleMenuPageEn = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [showNameModal, setShowNameModal] = useState(false);
  const [guestName, setGuestName] = useState('');
  const [merkabaOpacity, setMerkabaOpacity] = useState(0);
  const [merkabaRotation, setMerkabaRotation] = useState(0);
  const [titleGlowIntensity, setTitleGlowIntensity] = useState(0.2);

  const displayName = user?.name || guestName;

  useEffect(() => {
    const savedGuestName = localStorage.getItem('track_user_name');
    if (savedGuestName) {
      setGuestName(savedGuestName);
    }
    const nameModalDismissed = sessionStorage.getItem('nameModalDismissed');
    if (!user && !nameModalDismissed && !savedGuestName) {
      const timer = setTimeout(() => setShowNameModal(false), 7000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  useEffect(() => {
    let currentRotation = 0;
    const animateMerkaba = () => {
      currentRotation = (currentRotation + 30) % 360;
      setMerkabaRotation(currentRotation); setMerkabaOpacity(0.5);
      const fadeOutTimer = setTimeout(() => setMerkabaOpacity(0), 2000);
      const loopTimer = setTimeout(animateMerkaba, 3500);
      return () => { clearTimeout(fadeOutTimer); clearTimeout(loopTimer); };
    };
    const initialDelay = setTimeout(animateMerkaba, 500);
    return () => clearTimeout(initialDelay);
  }, []);

  useEffect(() => {
    const animateGlow = () => setTitleGlowIntensity(prev => (prev === 0.2 ? 0.4 : 0.2));
    const glowInterval = setInterval(animateGlow, 3000);
    return () => clearInterval(glowInterval);
  }, []);

  const handleNameSubmit = (name) => {
    localStorage.setItem('track_user_name', name);
    setGuestName(name);
    setShowNameModal(false);
    sendCodedLog({ message: 'Guest added their name', event: 'guest_name_set', ...Common.getCommonLogData(location) });
  };

  const handleModalClose = () => {
    sessionStorage.setItem('nameModalDismissed', 'true');
    setShowNameModal(false);
    sendCodedLog({ message: 'Guest dismissed the name modal', event: 'guest_modal_name_dismiss', ...Common.getCommonLogData(location) });
  };

  const handleLinktreeButtonClick = (title, path) => {
    const eventMap = { 
        "Portal Oracle": { event: 'home_portal_oracle_button_click_en', msg: 'Click on "Portal Oracle" button' },
        "Going Gamma": { event: 'home_going_gamma_button_click_en', msg: 'Click on "Going Gamma" button' }, 
        "Bashar Transmissions": { event: 'home_bashar_button_click_en', msg: 'Click on "Bashar Transmissions" button' }, 
        "Manifestation Technics Book": { event: 'home_manifestation_book_button_click_en', msg: 'Click on "Manifestation Technics Book" button' }, 
        "Continue My Course": { event: 'home_continue_course_button_click_en', msg: 'Click on "Continue My Course" button' } 
    };
    const logData = eventMap[title] || { event: 'home_generic_button_click_en', msg: `Click on "${title}" button` };
    sendCodedLog({ message: `${logData.msg} (${path})`, event: logData.event, ...Common.getCommonLogData(location) });
  };
  
  const handleProfileImageClick = () => sendCodedLog({ message: 'Click on profile image', event: 'home_profile_image_click_en', ...Common.getCommonLogData(location) });
  
  const handleSocialMediaClick = (platform, url) => {
    sendCodedLog({ message: `Social media link click (Home): ${platform}`, event: `home_social_media_click_${platform.toLowerCase()}_en`, ...Common.getCommonLogData(location) });
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  
  const handleInstagramClick = (e) => { e.preventDefault(); handleSocialMediaClick('Instagram', 'https://instagram.com/quantum.gus'); };
  const handleTiktokClick = (e) => { e.preventDefault(); handleSocialMediaClick('TikTok', 'https://www.tiktok.com/@quantum.gus'); };
  const handleYoutubeClick = (e) => { e.preventDefault(); handleSocialMediaClick('YouTube', 'https://www.youtube.com/@imanifesti'); };

  const menuItems = [...memenuItems, ...baseMenuItems];

  return (
    <div className="bg-black text-white min-h-screen font-sans flex flex-col">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-neutral-900 to-black"></div>

      <main className="relative z-10 flex-grow container mx-auto px-4 py-12 flex flex-col items-center">

        <div className="relative flex items-center justify-between mb-8 w-full max-w-2xl mx-auto">
          <h1 className="relative z-10 text-3xl sm:text-6xl font-bold leading-none text-left mt-7 mb-7" style={{ fontFamily: '"Cinzel Decorative", serif', backgroundImage: 'linear-gradient(to bottom, #FFEC8B, #FFD700, #DAA520, #B8860B)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', textShadow: `0 0 8px rgba(255, 215, 0, ${titleGlowIntensity}), 0 0 12px rgba(255, 215, 0, ${titleGlowIntensity / 2})`, transition: 'text-shadow 1.5s ease-in-out' }}>
            Portal
          </h1>
          <img src={merkabaImg} alt="Golden Merkaba" className="absolute z-0 w-8 h-8 sm:w-12 sm:h-12 object-contain rounded-full transition-all duration-1000 ease-in-out" style={{ opacity: merkabaOpacity, top: '50%', left: '50%', transform: `translate(-50%, -50%) rotate(${merkabaRotation}deg)` }} />
          <h1 className="relative z-10 text-3xl sm:text-6xl font-bold leading-none text-right mt-7 mb-7" style={{ fontFamily: '"Cinzel Decorative", serif', backgroundImage: 'linear-gradient(to bottom, #FFEC8B, #FFD700, #DAA520, #B8860B)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', textShadow: `0 0 8px rgba(255, 215, 0, ${titleGlowIntensity}), 0 0 12px rgba(255, 215, 0, ${titleGlowIntensity / 2})`, transition: 'text-shadow 1.5s ease-in-out' }}>
            Oracle
          </h1>
        </div>

        <div className="text-center mb-10">
            <div className="relative flex items-baseline justify-center gap-2">
                <h2 className="relative z-10 text-2xl sm:text-2xl font-bold leading-none" style={{ fontFamily: '"Cinzel Decorative", serif', backgroundImage: 'linear-gradient(to bottom, #FFEC8B, #c4c1b3ff, #e4d4adff, #e7d9b6ff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    Hi,
                </h2>
                <h1 className="relative z-10 text-3xl sm:text-4xl font-bold leading-none" style={{ fontFamily: '"Cinzel Decorative", serif', backgroundImage: 'linear-gradient(to bottom, #FFEC8B, #c4c1b3ff, #e4d4adff, #e7d9b6ff)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', textShadow: `0 0 8px rgba(255, 215, 0, ${titleGlowIntensity}), 0 0 12px rgba(255, 215, 0, ${titleGlowIntensity / 2})`, transition: 'text-shadow 1.5s ease-in-out' }}>
                    {displayName || 'Traveller'}
                </h1>
            </div>
            <p className="text-sm sm:text-base text-gray-400 mt-3 max-w-sm mx-auto">
                {user
                    ? "Resume your transformation journey."
                    : guestName
                        ? `Welcome, ${guestName}! Get guidance through the Portal Oracle.`
                        : "Get guidance through the Portal Oracle."
                }
            </p>
        </div>
        
        <div className="w-full flex flex-col items-center">
          {menuItems.map((item, index) => (
            <LinktreeButton key={item.path + index} item={item} index={index} onButtonClick={handleLinktreeButtonClick} />
          ))}
        </div>

        {!displayName && (
            <div className="flex flex-col items-center mt-12 mb-8">
                <img
                    src={perfilImg}
                    alt="Profile" 
                    className="w-24 h-24 sm:w-28 sm:h-28 rounded-full mb-2 object-cover border-4 border-gray-700 shadow-lg cursor-pointer"
                    onClick={handleProfileImageClick}
                />
                <h2 className="text-xl sm:text-2xl font-bold mb-0.5 text-white">
                    Gustavo • Aguiar
                </h2>
                <p className="text-sm sm:text-base text-gray-400 text-center max-w-sm mb-8">
                    Writer & Coach <br/> I explain the 5 Universal Laws.
                </p>
            </div>
        )}

        <div className="flex justify-center gap-6 text-2xl text-gray-300 mt-8">
          <a onClick={handleInstagramClick} aria-label="Instagram" className="hover:text-white cursor-pointer"><FaInstagram /></a>
          <a onClick={handleTiktokClick} aria-label="TikTok" className="hover:text-white cursor-pointer"><FaTiktok /></a>
          <a onClick={handleYoutubeClick} aria-label="YouTube" className="hover:text-white cursor-pointer"><FaYoutube /></a>
        </div>
      </main>

      <footer className="relative z-10 text-center py-6 text-xs text-gray-600">
        <p>© {new Date().getFullYear()} Gus • Aguiar | On a mission to elevate and transform.</p>
      </footer>

      {showNameModal && <NameModal onClose={handleModalClose} onSubmitName={handleNameSubmit} />}
    </div>
  );
};

export default OracleMenuPageEn;